import React, { useState, useEffect } from 'react';
import { getRegionList } from '../../lib/api/region-api';
import { getSidoList, getSigunguList, getBjdongList } from '../../lib/api/bjd-api';
import './RegionTab.css';

function RegionTab() {
  const [regions, setRegions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [sidoList, setSidoList] = useState([]);
  const [sigunguList, setSigunguList] = useState([]);
  const [bjdongList, setBjdongList] = useState([]);

  const [selectedSido, setSelectedSido] = useState('');
  const [selectedSigungu, setSelectedSigungu] = useState('');
  const [selectedBjdong, setSelectedBjdong] = useState('');

  // 헤더 접기/펼치기 상태
  const [expandedGroups, setExpandedGroups] = useState({
    population: false,
    job: false,
    traffic: false,
    school: false,
    env: false,
    supply: false,
    average: false
  });

  const [filters, setFilters] = useState({
    sidoName: '',
    sigunguName: '',
    bjdCode: '',
    snapshotDate: null,
    pageNumber: 1,
    pageSize: 100,
  });

  useEffect(() => {
    const loadSidoList = async () => {
      try {
        const data = await getSidoList();
        setSidoList(data);
      } catch (err) {
        console.error('시도 목록 로드 실패:', err);
      }
    };
    loadSidoList();
  }, []);

  useEffect(() => {
    if (selectedSido) {
      const loadSigunguList = async () => {
        try {
          const data = await getSigunguList(selectedSido);
          setSigunguList(data);
          setBjdongList([]);
          setSelectedSigungu('');
          setSelectedBjdong('');
        } catch (err) {
          console.error('시군구 목록 로드 실패:', err);
        }
      };
      loadSigunguList();
    } else {
      setSigunguList([]);
      setBjdongList([]);
      setSelectedSigungu('');
      setSelectedBjdong('');
    }
  }, [selectedSido]);

  useEffect(() => {
    if (selectedSigungu) {
      const loadBjdongList = async () => {
        try {
          const data = await getBjdongList(selectedSigungu);
          setBjdongList(data);
          setSelectedBjdong('');
        } catch (err) {
          console.error('법정동 목록 로드 실패:', err);
        }
      };
      loadBjdongList();
    } else {
      setBjdongList([]);
      setSelectedBjdong('');
    }
  }, [selectedSigungu]);

  useEffect(() => {
    const loadData = async () => {
      if (filters.sidoName || filters.sigunguName || filters.bjdCode) {
        setLoading(true);
        setError(null);

        try {
          const data = await getRegionList(filters);
          setRegions(data);
        } catch (err) {
          setError(err.message || '데이터를 불러오는데 실패했습니다.');
          console.error('지역 데이터 로드 실패:', err);
        } finally {
          setLoading(false);
        }
      }
    };
    loadData();
  }, [filters]);

  const handleSearch = () => {
    const params = {
      pageNumber: 1,
      pageSize: 100
    };

    if (selectedBjdong) {
      const bjdong = bjdongList.find(item => item.bjdCode === selectedBjdong);
      if (bjdong) {
        params.sidoName = bjdong.sidoName;
        params.sigunguName = bjdong.sigunguName;
        params.bjdCode = bjdong.bjdCode;
      }
    } else if (selectedSigungu) {
      const sigungu = sigunguList.find(item => item.hjdCode === selectedSigungu);
      if (sigungu) {
        params.sidoName = sigungu.sidoName;
        params.sigunguName = sigungu.sigunguName;
      }
    } else if (selectedSido) {
      const sido = sidoList.find(item => item.hjdCode === selectedSido);
      if (sido) {
        params.sidoName = sido.sidoName;
      }
    }

    setFilters(params);
  };

  const handleReset = () => {
    setSelectedSido('');
    setSelectedSigungu('');
    setSelectedBjdong('');
    setSigunguList([]);
    setBjdongList([]);
    setRegions([]);
    setFilters({
      sidoName: '',
      sigunguName: '',
      bjdCode: '',
      snapshotDate: null,
      pageNumber: 1,
      pageSize: 100,
    });
  };

  const toggleGroup = (groupName) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupName]: !prev[groupName]
    }));
  };

  const getGradeColor = (grade) => {
    if (!grade) return '';
    const gradeStr = String(grade);
    const gradeUpper = gradeStr.toUpperCase();
    if (gradeUpper.includes('S')) return 'grade-s';
    if (gradeUpper === 'A') return 'grade-a';
    if (gradeUpper === 'B') return 'grade-b';
    if (gradeUpper === 'C') return 'grade-c';
    return '';
  };

  return (
    <div className="region-tab">
      <div className="region-filters">
        <div className="filter-group">
          <label>시도</label>
          <select value={selectedSido} onChange={(e) => setSelectedSido(e.target.value)}>
            <option value="">시도 선택</option>
            {sidoList.map((sido) => (
              <option key={sido.hjdCode} value={sido.hjdCode}>
                {sido.sidoName}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>시군구</label>
          <select value={selectedSigungu} onChange={(e) => setSelectedSigungu(e.target.value)} disabled={!selectedSido}>
            <option value="">시군구 선택</option>
            {sigunguList.map((sigungu) => (
              <option key={sigungu.hjdCode} value={sigungu.hjdCode}>
                {sigungu.sigunguName}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>법정동</label>
          <select value={selectedBjdong} onChange={(e) => setSelectedBjdong(e.target.value)} disabled={!selectedSigungu}>
            <option value="">법정동 선택</option>
            {bjdongList.map((bjdong) => (
              <option key={bjdong.bjdCode} value={bjdong.bjdCode}>
                {bjdong.bjdName}
              </option>
            ))}
          </select>
        </div>

        <button className="filter-search" onClick={handleSearch}>
          검색
        </button>

        <button className="filter-reset" onClick={handleReset}>
          초기화
        </button>
      </div>

      {loading && <div className="region-loading">데이터를 불러오는 중...</div>}
      {error && <div className="region-error">{error}</div>}

      {!loading && !error && regions.length > 0 && (
        <div className="region-table-wrapper">
          <table className="region-table">
            <thead>
              <tr>
                <th rowSpan="2" className="group-header col-region">지역</th>
                <th rowSpan="2" className="group-header col-grade">급지</th>
                <th colSpan="2" className="group-header">생활권</th>
                <th colSpan="2" className="group-header">규제현황</th>

                <th
                  colSpan={expandedGroups.population ? 4 : 1}
                  className="group-header expandable"
                  onClick={() => toggleGroup('population')}
                  style={{ cursor: 'pointer' }}
                >
                  인구 {expandedGroups.population ? '−' : '+'}
                </th>

                <th
                  colSpan={expandedGroups.job ? 2 : 1}
                  className="group-header expandable"
                  onClick={() => toggleGroup('job')}
                  style={{ cursor: 'pointer' }}
                >
                  직장 {expandedGroups.job ? '−' : '+'}
                </th>

                <th
                  colSpan={expandedGroups.traffic ? 4 : 1}
                  className="group-header expandable"
                  onClick={() => toggleGroup('traffic')}
                  style={{ cursor: 'pointer' }}
                >
                  교통 {expandedGroups.traffic ? '−' : '+'}
                </th>

                <th
                  colSpan={expandedGroups.school ? 6 : 1}
                  className="group-header expandable"
                  onClick={() => toggleGroup('school')}
                  style={{ cursor: 'pointer' }}
                >
                  학군 {expandedGroups.school ? '−' : '+'}
                </th>

                <th
                  colSpan={expandedGroups.env ? 4 : 1}
                  className="group-header expandable"
                  onClick={() => toggleGroup('env')}
                  style={{ cursor: 'pointer' }}
                >
                  환경 {expandedGroups.env ? '−' : '+'}
                </th>

                <th
                  colSpan={expandedGroups.supply ? 6 : 1}
                  className="group-header expandable"
                  onClick={() => toggleGroup('supply')}
                  style={{ cursor: 'pointer' }}
                >
                  공급 {expandedGroups.supply ? '−' : '+'}
                </th>

                <th rowSpan="2" className="group-header">호재</th>

                <th
                  colSpan={expandedGroups.average ? 3 : 1}
                  className="group-header expandable"
                  onClick={() => toggleGroup('average')}
                  style={{ cursor: 'pointer' }}
                >
                  지역평균 {expandedGroups.average ? '−' : '+'}
                </th>
              </tr>
              <tr>
                <th className="col-name">동</th>
                <th className="col-grade">급지</th>
                <th className="col-text">규제</th>
                <th className="col-date">규제일</th>

                {/* 인구 그룹 */}
                {expandedGroups.population ? (
                  <>
                    <th className="col-grade">등급</th>
                    <th className="col-number">인구</th>
                    <th className="col-number">세대수</th>
                    <th className="col-number">전출입</th>
                  </>
                ) : (
                  <th className="col-grade">등급</th>
                )}

                {/* 직장 그룹 */}
                {expandedGroups.job ? (
                  <>
                    <th className="col-grade">등급</th>
                    <th className="col-number">직장수</th>
                  </>
                ) : (
                  <th className="col-grade">등급</th>
                )}

                {/* 교통 그룹 */}
                {expandedGroups.traffic ? (
                  <>
                    <th className="col-grade">등급</th>
                    <th className="col-text">강남접근</th>
                    <th className="col-text">주요거점</th>
                    <th className="col-text">지하철</th>
                  </>
                ) : (
                  <th className="col-grade">등급</th>
                )}

                {/* 학군 그룹 */}
                {expandedGroups.school ? (
                  <>
                    <th className="col-grade">등급</th>
                    <th className="col-number">95점이상</th>
                    <th className="col-number">90점이상</th>
                    <th className="col-number">85점이상</th>
                    <th className="col-number">85점미만</th>
                    <th className="col-text">중학군</th>
                  </>
                ) : (
                  <th className="col-grade">등급</th>
                )}

                {/* 환경 그룹 */}
                {expandedGroups.env ? (
                  <>
                    <th className="col-grade">등급</th>
                    <th className="col-text">백화점</th>
                    <th className="col-text">대형마트</th>
                    <th className="col-text">기타</th>
                  </>
                ) : (
                  <th className="col-grade">등급</th>
                )}

                {/* 공급 그룹 */}
                {expandedGroups.supply ? (
                  <>
                    <th className="col-grade">등급</th>
                    <th className="col-number">준공</th>
                    <th className="col-number">준공예정</th>
                    <th className="col-number">사전</th>
                    <th className="col-number">사업승인</th>
                    <th className="col-number">총합</th>
                  </>
                ) : (
                  <th className="col-grade">등급</th>
                )}

                {/* 지역평균 그룹 */}
                {expandedGroups.average ? (
                  <>
                    <th className="col-percent">전세가율</th>
                    <th className="col-number">매매거래량</th>
                    <th className="col-number">전세거래량</th>
                  </>
                ) : (
                  <th className="col-text">평균</th>
                )}
              </tr>
            </thead>
            <tbody>
              {regions.map((region, index) => (
                <tr key={`${region.bjdCode}-${region.snapshotId || index}`}>
                  <td className="text-left col-region">{region.sidoName} {region.sigunguName}</td>
                  <td className={`text-center grade-cell ${getGradeColor(region.regionGrade)}`}>
                    {region.regionGrade || '-'}
                  </td>
                  <td className="text-left">{region.dongName || region.bjdName || '-'}</td>
                  <td className={`text-center grade-cell ${getGradeColor(region.dongGrade)}`}>
                    {region.dongGrade || '-'}
                  </td>
                  <td className="text-center">{region.regulation || '-'}</td>
                  <td className="text-center">{region.regulationDate || '-'}</td>

                  {/* 인구 그룹 */}
                  {expandedGroups.population ? (
                    <>
                      <td className={`text-center grade-cell ${getGradeColor(region.popGrade)}`}>
                        {region.popGrade || '-'}
                      </td>
                      <td className="text-right">{region.population ? region.population.toLocaleString() : '-'}</td>
                      <td className="text-right">{region.households ? region.households.toLocaleString() : '-'}</td>
                      <td className="text-right">{region.migration ? region.migration.toLocaleString() : '-'}</td>
                    </>
                  ) : (
                    <td className={`text-center grade-cell ${getGradeColor(region.popGrade)}`}>
                      {region.popGrade || '-'}
                    </td>
                  )}

                  {/* 직장 그룹 */}
                  {expandedGroups.job ? (
                    <>
                      <td className={`text-center grade-cell ${getGradeColor(region.jobGrade)}`}>
                        {region.jobGrade || '-'}
                      </td>
                      <td className="text-right">{region.jobCount ? region.jobCount.toLocaleString() : '-'}</td>
                    </>
                  ) : (
                    <td className={`text-center grade-cell ${getGradeColor(region.jobGrade)}`}>
                      {region.jobGrade || '-'}
                    </td>
                  )}

                  {/* 교통 그룹 */}
                  {expandedGroups.traffic ? (
                    <>
                      <td className={`text-center grade-cell ${getGradeColor(region.trafficGrade)}`}>
                        {region.trafficGrade || '-'}
                      </td>
                      <td className="text-center">{region.trafficGangnam || '-'}</td>
                      <td className="text-center">{region.trafficMajor || '-'}</td>
                      <td className="text-center">{region.trafficSubway || '-'}</td>
                    </>
                  ) : (
                    <td className={`text-center grade-cell ${getGradeColor(region.trafficGrade)}`}>
                      {region.trafficGrade || '-'}
                    </td>
                  )}

                  {/* 학군 그룹 */}
                  {expandedGroups.school ? (
                    <>
                      <td className={`text-center grade-cell ${getGradeColor(region.schoolGrade)}`}>
                        {region.schoolGrade || '-'}
                      </td>
                      <td className="text-right">{region.school95 || '-'}</td>
                      <td className="text-right">{region.school90 || '-'}</td>
                      <td className="text-right">{region.school85 || '-'}</td>
                      <td className="text-right">{region.schoolBelow || '-'}</td>
                      <td className="text-center">{region.schoolMiddleAcademy || '-'}</td>
                    </>
                  ) : (
                    <td className={`text-center grade-cell ${getGradeColor(region.schoolGrade)}`}>
                      {region.schoolGrade || '-'}
                    </td>
                  )}

                  {/* 환경 그룹 */}
                  {expandedGroups.env ? (
                    <>
                      <td className={`text-center grade-cell ${getGradeColor(region.envGrade)}`}>
                        {region.envGrade || '-'}
                      </td>
                      <td className="text-center">{region.envDepartment || '-'}</td>
                      <td className="text-center">{region.envMart || '-'}</td>
                      <td className="text-center">{region.envEtc || '-'}</td>
                    </>
                  ) : (
                    <td className={`text-center grade-cell ${getGradeColor(region.envGrade)}`}>
                      {region.envGrade || '-'}
                    </td>
                  )}

                  {/* 공급 그룹 */}
                  {expandedGroups.supply ? (
                    <>
                      <td className={`text-center grade-cell ${getGradeColor(region.supplyGrade)}`}>
                        {region.supplyGrade || '-'}
                      </td>
                      <td className="text-right">{region.supplyCompleted ? region.supplyCompleted.toLocaleString() : '-'}</td>
                      <td className="text-right">{region.supplyScheduled ? region.supplyScheduled.toLocaleString() : '-'}</td>
                      <td className="text-right">{region.supplyPreannounced ? region.supplyPreannounced.toLocaleString() : '-'}</td>
                      <td className="text-right">{region.supplyApproved ? region.supplyApproved.toLocaleString() : '-'}</td>
                      <td className="text-right">{region.supplyTotal ? region.supplyTotal.toLocaleString() : '-'}</td>
                    </>
                  ) : (
                    <td className={`text-center grade-cell ${getGradeColor(region.supplyGrade)}`}>
                      {region.supplyGrade || '-'}
                    </td>
                  )}

                  <td className="text-center">{region.issueSign || '-'}</td>

                  {/* 지역평균 그룹 */}
                  {expandedGroups.average ? (
                    <>
                      <td className="text-right">{region.avgJeonseRatio ? `${region.avgJeonseRatio}%` : '-'}</td>
                      <td className="text-right">{region.avgMaemaeTxCnt ? region.avgMaemaeTxCnt.toLocaleString() : '-'}</td>
                      <td className="text-right">{region.avgJeonseTxCnt ? region.avgJeonseTxCnt.toLocaleString() : '-'}</td>
                    </>
                  ) : (
                    <td className="text-center">-</td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default RegionTab;
